import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSelector } from "src/store";
import { setPaneTab, showDetails, updateSearchQuery } from "src/ui/containers/dashboard/devices/store";
import { selectDevicesByGroupIds } from "src/ui/containers/dashboard/devices/store/selectors/devices";
import { MinimalTableCell } from "src/ui/shared/table/MinimalTableComponents";
import { showErrorToast } from "src/ui/shared/toasts/Toasts";

import { Device, DeviceState } from "@dashboard/devices/types";
import { setColumnVisibility } from "@dashboard/devices/utils/columnVisibility";
import { selectGroups } from "@dashboard/groups/store/selectors";
import ElasticService from "@services/elasticService";
import UserSettingsService from "@services/userSettingsService";
import QueryBuilder from "@shared/form/QueryBuilderField/QueryBuilderField.utils";
import { TextLink } from "@shared/TextLink";

import { TransformedApp } from "./ManagedAppsCard";

const hasCorrectPackage = (app: TransformedApp, packages: NonNullable<DeviceState["packages"]>) => {
    return packages.some((item) => item.package === app.package && item.versionCode === app.versionCode);
};

const getDeviceWithCorrectApp = (app: TransformedApp, devices: (Device | null)[]) => {
    return devices.find((item) => Array.isArray(item?.lastState?.packages) && hasCorrectPackage(app, item.lastState.packages));
};

type TableItemProps = TransformedApp & {
    filteredGroupIds: number[];
};

const TableItem = (props: TableItemProps) => {
    const { t } = useTranslation(["common", "pages"]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const groups = useSelector(selectGroups);
    const filteredDevices = useSelector((state) => selectDevicesByGroupIds(state, props.filteredGroupIds));
    const filteredGroups = groups.filter((group) => props.filteredGroupIds.includes(group.id));

    const handleLicensesOnClick = () => {
        const equalHash = QueryBuilder.getEqualHash();
        const filters = QueryBuilder.parseFiltersToString([
            {
                key: "device.lastState.packages.versionCode",
                operator: equalHash,
                value: String(props.versionCode),
            },
            { key: "device.lastState.packages.package", operator: equalHash, value: props.package },
        ]);
        const isGroupSelectionNarrowed = props.filteredGroupIds.length !== groups.length;
        const columnFilters = isGroupSelectionNarrowed
            ? [{ id: "groupName", value: props.filteredGroupIds.map((id) => id.toString()) }]
            : [];
        UserSettingsService.setWorkspaceValue("devicePage.columnFilters", columnFilters);
        if (isGroupSelectionNarrowed) {
            setColumnVisibility("groupName");
        }

        dispatch(updateSearchQuery(filters));
        navigate(`/dashboard/devices?search=${encodeURIComponent(filters)}`);
    };

    const handleLicenseOnClick = (app: TransformedApp) => {
        const device = getDeviceWithCorrectApp(app, filteredDevices);
        const group = device && filteredGroups.find((g) => g.licenseIds.includes(device.licenseId));
        if (!device) {
            showErrorToast(t("pages:overviewPage.cards.managedApps.toast.deviceNotFound"));
            return;
        }

        if (!group) {
            showErrorToast(t("pages:overviewPage.cards.managedApps.toast.groupNotFound"));
            return;
        }

        dispatch(setPaneTab("APPS"));
        dispatch(showDetails({ type: "license", selectedId: device.licenseId }));
        handleLicensesOnClick();
        ElasticService.createTransaction("overviewPageLinkClick", {
            tab: "managed-applications",
            text: props.count,
        });
    };

    if (props.count === 1) {
        return (
            <MinimalTableCell align="right">
                <TextLink onClick={() => handleLicenseOnClick(props)} tooltip={t("common:tooltip.showDevices", { count: 1 })}>
                    {props.count}
                </TextLink>
            </MinimalTableCell>
        );
    }

    return (
        <MinimalTableCell align="right">
            <TextLink onClick={handleLicensesOnClick} tooltip={t("common:tooltip.showDevices", { count: props.count })}>
                {props.count}
            </TextLink>
        </MinimalTableCell>
    );
};

export default TableItem;
